export type BotState =
  | "active"
  | "paused"
  | "deactivated"
  | "quota_exhausted"
  | "deleted"
  | "hard_deleted";

export type ProcessingStatus = "pending" | "processing" | "complete" | "failed";

export type ModelId =
  | "anthropic.claude-3-haiku-20240307-v1:0"
  | "anthropic.claude-3-sonnet-20240229-v1:0";

export interface ModelConfig {
  model_id: ModelId;
  max_tokens: number;
  temperature: number;
}

export interface RetrievalConfig {
  top_k: number;
  score_threshold: number;
  chunk_size: number;
  chunk_overlap: number;
}

export interface UiConfig {
  primary_color: string;
  welcome_message: string;
  placeholder_text: string;
  show_sources: boolean;
}

export interface RateLimits {
  per_ip_daily_cap: number;
  per_session_cap: number;
  session_limit_message: string;
  session_limit_cta?: string;
}

export const EMBED_FONTS = [
  "Inter", "Roboto", "Open Sans", "Lato", "Poppins",
  "Nunito", "DM Sans", "Plus Jakarta Sans", "Montserrat",
  "Work Sans", "Raleway", "Source Sans 3", "Geist",
] as const;
export type EmbedFont = typeof EMBED_FONTS[number];

export interface ChatWidgetTheme {
  primary_color: string;       // hex, e.g. "#5C6BC0"
  background_color: string;    // hex, e.g. "#FFFFFF"
  font_family: EmbedFont;
  border_radius: number;       // 0–24 px
  preset_name?: string;        // "Default" | "Ocean" | "Emerald" | "Rose" | "Violet" | "Custom"
  dark_mode?: boolean;         // dark background / light text
}

export const WIDGET_THEME_PRESETS: Record<string, Omit<ChatWidgetTheme, "preset_name">> = {
  Default: { primary_color: "#5C6BC0", background_color: "#FFFFFF", font_family: "Inter",    border_radius: 10 },
  Ocean:   { primary_color: "#2196F3", background_color: "#FFFFFF", font_family: "Roboto",   border_radius: 10 },
  Emerald: { primary_color: "#10B981", background_color: "#FFFFFF", font_family: "Poppins",  border_radius: 14 },
  Rose:    { primary_color: "#E11D48", background_color: "#FFFFFF", font_family: "Lato",     border_radius: 10 },
  Violet:  { primary_color: "#7C3AED", background_color: "#FFFFFF", font_family: "Nunito",   border_radius: 16 },
  Slate:   { primary_color: "#475569", background_color: "#F8FAFC", font_family: "Work Sans", border_radius: 6  },
};

export interface EmbedConfig {
  enabled: boolean;
  allowed_domains: string[];
  theme: "light" | "dark" | "auto";
  position: "bottom-right" | "bottom-left";
  welcome_message: string;
  per_session_cap: number;
  per_ip_daily_cap: number;
  cta_on_limit?: string;
  show_branding: boolean;
  widget_theme?: ChatWidgetTheme;
}

export interface Bot {
  bot_id: string;
  owner_id: string;
  name: string;
  slug: string;
  description?: string;
  state: BotState;
  system_prompt: string;
  model_config: ModelConfig;
  retrieval_config: RetrievalConfig;
  ui_config: UiConfig;
  rate_limits: RateLimits;
  embed_config: EmbedConfig;
  invite_required: boolean;
  document_count: number;
  total_chunks: number;
  processing_status: ProcessingStatus;
  created_at: string;
  updated_at: string;
}

export type DocumentStatus = "pending" | "processing" | "complete" | "failed";

export interface DocumentRecord {
  filename: string;
  size_bytes: number;
  last_modified: string;
  status: DocumentStatus;
  chunk_count?: number;
  error_message?: string;
  ingested_at?: string;
}

export interface PublicBotConfig {
  bot_id: string;
  name: string;
  slug: string;
  state: BotState;
  ui_config: UiConfig;
  invite_required: boolean;
  embed_config: Pick<EmbedConfig, "theme" | "position" | "welcome_message" | "show_branding">;
  suggested_triggers?: import("./workflow.js").SuggestedTrigger[];
}
